import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";

import LoadingProgress from "../components/LoadingProgress";
import ErrorMessage from "../components/ErrorMessage";
import { fetchBowler } from "../actions/bowler";
import "../styles/DashboardView.css";
import "../styles/BowlerView.css";

// ── helpers ──────────────────────────────────────────────────────────────────

function localIso(d) {
  if (!(d instanceof Date) || isNaN(d)) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function fmtHours(h) {
  if (h === null || h === undefined) return "—";
  const hours = Math.round(h * 10) / 10;
  if (hours < 24) return `${hours}h`;
  const days = Math.round((hours / 24) * 10) / 10;
  return `${days}d`;
}

function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  return `${Math.round(v)}%`;
}

// ── palette ───────────────────────────────────────────────────────────────────

const PRIORITY_COLORS = {
  LOW: "#10b981",
  MEDIUM: "#f59e0b",
  HIGH: "#ef4444",
  CRITICAL: "#7c3aed",
};

const STATUS_COLORS_TICKET = {
  OPEN: "#ef4444",
  IN_REVIEW: "#f59e0b",
  IN_INCIDENCE: "#8b5cf6",
  IN_REPAIR: "#3b82f6",
  RESOLVED: "#10b981",
  CLOSED: "#6b7280",
};

const STATUS_COLORS_INC = {
  OPEN: "#ef4444",
  IN_REVIEW: "#f59e0b",
  IN_REPAIR: "#3b82f6",
  RESOLVED: "#10b981",
};

const PRIORITY_ORDER = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

// Nice Spanish labels
const PRIORITY_LABELS = { LOW: "Baja", MEDIUM: "Media", HIGH: "Alta", CRITICAL: "Crítica" };
const STATUS_LABELS_TICKET = {
  OPEN: "Abierto",
  IN_REVIEW: "En revisión",
  IN_INCIDENCE: "En incidencia",
  IN_REPAIR: "En reparación",
  RESOLVED: "Resuelto",
  CLOSED: "Cerrado",
};
const STATUS_LABELS_INC = {
  OPEN: "Abierta",
  IN_REVIEW: "En revisión",
  IN_REPAIR: "En reparación",
  RESOLVED: "Resuelta",
};

// ── sub-components ────────────────────────────────────────────────────────────

function KpiCard({ value, label, colorClass, icon }) {
  return (
    <div className={`dashboard-kpi-card ${colorClass}`}>
      <div className="kpi-icon">
        <i className={`fas fa-${icon}`} />
      </div>
      <div className="kpi-content">
        <div className="kpi-label">{label}</div>
        <div className="kpi-value">{value}</div>
      </div>
    </div>
  );
}

function SectionTitle({ icon, title, subtitle }) {
  return (
    <div className="bowler-section-title">
      <i className={`fas fa-${icon} bowler-section-icon`} />
      <div>
        <span className="bowler-section-main">{title}</span>
        {subtitle && <span className="bowler-section-sub">{subtitle}</span>}
      </div>
    </div>
  );
}

// SLA compliance row per priority level
function SlaComplianceBar({ label, rate, color }) {
  const rateVal = rate !== null && rate !== undefined ? Math.round(rate) : null;
  const barColor = rateVal === null ? "#e5e7eb" : rateVal >= 80 ? "#10b981" : rateVal >= 50 ? "#f59e0b" : "#ef4444";
  return (
    <div className="bowler-sla-row">
      <span className="bowler-sla-label" style={{ color }}>{label}</span>
      <div className="bowler-sla-bar-bg">
        <div
          className="bowler-sla-bar-fill"
          style={{ width: rateVal !== null ? `${rateVal}%` : "0%", background: barColor }}
        />
      </div>
      <span className="bowler-sla-pct" style={{ color: barColor }}>
        {rateVal !== null ? `${rateVal}%` : "S/D"}
      </span>
    </div>
  );
}

// Resolution time card per priority
function ResolutionTable({ data, title, icon }) {
  return (
    <div className="dashboard-card-section">
      <div className="card-header-custom" style={{ background: "#f8fafc" }}>
        <i className={`fas fa-${icon} mr-2`} style={{ color: "#0b97ff" }} />
        {title}
      </div>
      <div className="card-body-custom">
        <table className="bowler-resolution-table">
          <thead>
            <tr>
              <th>Urgencia</th>
              <th>Tiempo medio</th>
              <th>Tickets/Inc.</th>
            </tr>
          </thead>
          <tbody>
            {PRIORITY_ORDER.map(p => (
              <tr key={p}>
                <td>
                  <span className="bowler-priority-badge" style={{ background: PRIORITY_COLORS[p] }}>
                    {PRIORITY_LABELS[p]}
                  </span>
                </td>
                <td className="bowler-resolution-val">{fmtHours(data.avg[p])}</td>
                <td className="bowler-resolution-count">{data.count[p] || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── main class ────────────────────────────────────────────────────────────────

class BowlerView extends React.Component {
  constructor(props) {
    super(props);
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    this.state = {
      startDate: localIso(monthStart),
      endDate: localIso(today),
      tempStart: localIso(monthStart),
      tempEnd: localIso(today),
    };
  }

  componentDidMount() {
    this.load();
  }

  load() {
    const { startDate, endDate } = this.state;
    this.props.fetchBowler(startDate, endDate);
  }

  handleRefresh = () => {
    this.setState(
      { startDate: this.state.tempStart, endDate: this.state.tempEnd },
      () => this.load(),
    );
  };

  render() {
    const { bowlerData, bowlerLoading, bowlerError } = this.props;
    const { tempStart, tempEnd } = this.state;

    return (
      <div className="dashboard-view">
        {/* Header */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">
              <i className="fas fa-headset mr-2" style={{ color: "#0b97ff" }} />
              <span className="text-vw-dark">SAT </span>
              <span className="text-vw-light">Bowler</span>
            </h1>
            <span style={{ fontSize: 13, color: "#6b7280" }}>
              Dashboard de soporte · Tickets, Incidencias & SLA
            </span>
          </div>
          <div className="dashboard-actions">
            <input
              type="date"
              className="dashboard-filter-input"
              value={tempStart}
              onChange={e => this.setState({ tempStart: e.target.value })}
            />
            <span style={{ color: "#6b7280", fontSize: 13 }}>→</span>
            <input
              type="date"
              className="dashboard-filter-input"
              value={tempEnd}
              onChange={e => this.setState({ tempEnd: e.target.value })}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={this.handleRefresh}
              disabled={bowlerLoading}
            >
              <i className="fas fa-sync-alt mr-1" />
              Actualizar
            </button>
          </div>
        </div>

        {bowlerLoading && <LoadingProgress />}
        {bowlerError && <ErrorMessage message={bowlerError.message || "Error al cargar datos"} />}

        {!bowlerLoading && bowlerData && (
          <>
            {/* ── KPIs row ─────────────────────────────────────────── */}
            <div className="dashboard-kpi-wrapper mb-4">
              <div className="dashboard-kpi-row">
                <KpiCard value={bowlerData.totalTickets} label="Tickets totales" colorClass="total" icon="ticket-alt" />
                <KpiCard value={bowlerData.openTickets} label="Tickets abiertos" colorClass="red-block" icon="exclamation-circle" />
                <KpiCard value={bowlerData.resolvedTickets} label="Tickets resueltos" colorClass="green-block" icon="check-circle" />
                <KpiCard value={bowlerData.totalIncidences} label="Incidencias totales" colorClass="pinpad-block" icon="tools" />
                <KpiCard value={bowlerData.openIncidences} label="Incidencias abiertas" colorClass="broken" icon="wrench" />
                <KpiCard value={bowlerData.resolvedIncidences} label="Incidencias resueltas" colorClass="door-block" icon="check-double" />
              </div>
            </div>

            {/* ── Section: Evolución temporal ──────────────────────── */}
            <div className="dashboard-card-section mb-4">
              <div className="card-header-custom" style={{ background: "#f8fafc" }}>
                <SectionTitle icon="chart-line" title="Evolución temporal" subtitle="Tickets e Incidencias abiertas por día" />
              </div>
              <div className="card-body-custom">
                {bowlerData.evolutionSeries && bowlerData.evolutionSeries.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={bowlerData.evolutionSeries} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip labelFormatter={v => `Día: ${v}`} />
                      <Legend />
                      <Line type="monotone" dataKey="tickets" stroke="#0b97ff" strokeWidth={2} dot={false} name="Tickets" />
                      <Line type="monotone" dataKey="incidences" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Incidencias" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted text-center py-3">Sin datos para el rango seleccionado</p>
                )}
              </div>
            </div>

            {/* ── TICKETS: dos columnas ─────────────────────────────── */}
            <div className="row mb-4">
              {/* Tickets por estado */}
              <div className="col-lg-4 mb-3">
                <div className="dashboard-card-section h-100">
                  <div className="card-header-custom" style={{ background: "#f8fafc" }}>
                    <SectionTitle icon="ticket-alt" title="Tickets por estado" />
                  </div>
                  <div className="card-body-custom">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={Object.entries(bowlerData.ticketsByStatus).map(([k, v]) => ({ name: STATUS_LABELS_TICKET[k] || k, value: v }))}
                          cx="50%"
                          cy="50%"
                          outerRadius={75}
                          dataKey="value"
                          label={({ name, percent }) => percent > 0.03 ? `${name} ${(percent * 100).toFixed(0)}%` : ""}
                          labelLine={false}
                        >
                          {Object.keys(bowlerData.ticketsByStatus).map(k => (
                            <Cell key={k} fill={STATUS_COLORS_TICKET[k] || "#cbd5e1"} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, n) => [v, n]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Tickets por urgencia */}
              <div className="col-lg-4 mb-3">
                <div className="dashboard-card-section h-100">
                  <div className="card-header-custom" style={{ background: "#f8fafc" }}>
                    <SectionTitle icon="exclamation-triangle" title="Tickets por urgencia" />
                  </div>
                  <div className="card-body-custom">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        data={PRIORITY_ORDER.map(p => ({ name: PRIORITY_LABELS[p], value: bowlerData.ticketsByPriority[p] || 0, color: PRIORITY_COLORS[p] }))}
                        margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]} name="Tickets">
                          {PRIORITY_ORDER.map(p => (
                            <Cell key={p} fill={PRIORITY_COLORS[p]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Tickets por categoría */}
              <div className="col-lg-4 mb-3">
                <div className="dashboard-card-section h-100">
                  <div className="card-header-custom" style={{ background: "#f8fafc" }}>
                    <SectionTitle icon="tags" title="Tickets por tipo" subtitle="Top categorías" />
                  </div>
                  <div className="card-body-custom">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        layout="vertical"
                        data={(bowlerData.ticketsByCategory || []).slice(0, 8)}
                        margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#0b97ff" radius={[0, 4, 4, 0]} name="Tickets" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>

            {/* ── INCIDENCIAS: tres columnas ───────────────────────── */}
            <div className="row mb-4">
              {/* Incidencias por estado */}
              <div className="col-lg-4 mb-3">
                <div className="dashboard-card-section h-100">
                  <div className="card-header-custom" style={{ background: "#f8fafc" }}>
                    <SectionTitle icon="tools" title="Incidencias por estado" />
                  </div>
                  <div className="card-body-custom">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={Object.entries(bowlerData.incidencesByStatus).map(([k, v]) => ({ name: STATUS_LABELS_INC[k] || k, value: v }))}
                          cx="50%"
                          cy="50%"
                          outerRadius={75}
                          dataKey="value"
                          label={({ name, percent }) => percent > 0.03 ? `${name} ${(percent * 100).toFixed(0)}%` : ""}
                          labelLine={false}
                        >
                          {Object.keys(bowlerData.incidencesByStatus).map(k => (
                            <Cell key={k} fill={STATUS_COLORS_INC[k] || "#cbd5e1"} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Incidencias por urgencia */}
              <div className="col-lg-4 mb-3">
                <div className="dashboard-card-section h-100">
                  <div className="card-header-custom" style={{ background: "#f8fafc" }}>
                    <SectionTitle icon="exclamation-triangle" title="Incidencias por urgencia" />
                  </div>
                  <div className="card-body-custom">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        data={PRIORITY_ORDER.map(p => ({ name: PRIORITY_LABELS[p], value: bowlerData.incidencesByPriority[p] || 0, color: PRIORITY_COLORS[p] }))}
                        margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]} name="Incidencias">
                          {PRIORITY_ORDER.map(p => (
                            <Cell key={p} fill={PRIORITY_COLORS[p]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Incidencias por categoría */}
              <div className="col-lg-4 mb-3">
                <div className="dashboard-card-section h-100">
                  <div className="card-header-custom" style={{ background: "#f8fafc" }}>
                    <SectionTitle icon="tags" title="Incidencias por tipo" subtitle="Top categorías" />
                  </div>
                  <div className="card-body-custom">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        layout="vertical"
                        data={(bowlerData.incidencesByCategory || []).slice(0, 8)}
                        margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Incidencias" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
          </div>
            </div>

            {/* ── Tiempo medio de resolución ───────────────────────── */}
            <div className="row mb-4">
              <div className="col-lg-6 mb-3">
                <ResolutionTable
                  title="Tiempo medio resolución — Tickets"
                  icon="stopwatch"
                  data={{
                    avg: bowlerData.avgTicketResolution,
                    count: bowlerData.ticketsByPriority,
                  }}
                />
              </div>
              <div className="col-lg-6 mb-3">
                <ResolutionTable
                  title="Tiempo medio resolución — Incidencias"
                  icon="stopwatch"
                  data={{
                    avg: bowlerData.avgIncidenceResolution,
                    count: bowlerData.incidencesByPriority,
                  }}
                />
              </div>
            </div>

            {/* ── Resolución gráfica comparativa ───────────────────── */}
            <div className="dashboard-card-section mb-4">
              <div className="card-header-custom" style={{ background: "#f8fafc" }}>
                <SectionTitle icon="clock" title="Comparativa tiempo medio (horas)" subtitle="Tickets vs Incidencias por nivel de urgencia" />
              </div>
              <div className="card-body-custom">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={PRIORITY_ORDER.map(p => ({
                      name: PRIORITY_LABELS[p],
                      Tickets: bowlerData.avgTicketResolution[p] !== null ? +(bowlerData.avgTicketResolution[p] || 0).toFixed(1) : 0,
                      Incidencias: bowlerData.avgIncidenceResolution[p] !== null ? +(bowlerData.avgIncidenceResolution[p] || 0).toFixed(1) : 0,
                    }))}
                    margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} unit="h" />
                    <Tooltip formatter={v => [`${v}h`]} />
                    <Legend />
                    <Bar dataKey="Tickets" fill="#0b97ff" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Incidencias" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ── Cumplimiento SLA ─────────────────────────────────── */}
            <div className="row mb-4">
              <div className="col-lg-6 mb-3">
                <div className="dashboard-card-section h-100">
                  <div className="card-header-custom" style={{ background: "#f8fafc" }}>
                    <SectionTitle icon="shield-alt" title="Cumplimiento SLA — Tickets" subtitle="% tickets resueltos en tiempo SLA del cliente" />
                  </div>
                  <div className="card-body-custom">
                    {PRIORITY_ORDER.map(p => (
                      <SlaComplianceBar
                        key={p}
                        label={PRIORITY_LABELS[p]}
                        rate={bowlerData.ticketSlaRate[p]}
                        color={PRIORITY_COLORS[p]}
                      />
                    ))}
                    <p className="text-muted mt-2" style={{ fontSize: 11 }}>
                      "S/D" = sin datos suficientes para calcular cumplimiento SLA
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 mb-3">
                <div className="dashboard-card-section h-100">
                  <div className="card-header-custom" style={{ background: "#f8fafc" }}>
                    <SectionTitle icon="shield-alt" title="Cumplimiento SLA — Incidencias" subtitle="% incidencias resueltas en tiempo SLA del cliente" />
                  </div>
                  <div className="card-body-custom">
                    {PRIORITY_ORDER.map(p => (
                      <SlaComplianceBar
                        key={p}
                        label={PRIORITY_LABELS[p]}
                        rate={bowlerData.incidenceSlaRate[p]}
                        color={PRIORITY_COLORS[p]}
                      />
                    ))}
                    <p className="text-muted mt-2" style={{ fontSize: 11 }}>
                      "S/D" = sin datos suficientes para calcular cumplimiento SLA
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Radar: SLA compliance overview ───────────────────── */}
            <div className="dashboard-card-section mb-4">
              <div className="card-header-custom" style={{ background: "#f8fafc" }}>
                <SectionTitle icon="spider" title="Visión global SLA" subtitle="Radar de cumplimiento SLA por nivel de urgencia" />
              </div>
              <div className="card-body-custom d-flex justify-content-center">
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart
                    data={PRIORITY_ORDER.map(p => ({
                      subject: PRIORITY_LABELS[p],
                      Tickets: bowlerData.ticketSlaRate[p] !== null ? Math.round(bowlerData.ticketSlaRate[p]) : 0,
                      Incidencias: bowlerData.incidenceSlaRate[p] !== null ? Math.round(bowlerData.incidenceSlaRate[p]) : 0,
                    }))}
                  >
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
                    <Radar name="Tickets" dataKey="Tickets" stroke="#0b97ff" fill="#0b97ff" fillOpacity={0.2} />
                    <Radar name="Incidencias" dataKey="Incidencias" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.2} />
                    <Legend />
                    <Tooltip formatter={v => [`${v}%`]} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  bowlerData: state.bowler.data,
  bowlerLoading: state.bowler.loading,
  bowlerError: state.bowler.error,
  role: state.auth.role,
  type: state.auth.type,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ fetchBowler }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(BowlerView);
